import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { API } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import axios from 'axios';
import { ScrollText, Search, ChevronLeft, ChevronRight, Filter } from 'lucide-react';

const actionColors = {
  user_created: 'bg-blue-500/20 text-blue-400',
  invite_sent: 'bg-amber-500/20 text-amber-400',
  invite_resent: 'bg-amber-500/20 text-amber-400',
  invite_accepted: 'bg-green-500/20 text-green-400',
  password_reset_sent: 'bg-orange-500/20 text-orange-400',
  password_reset_completed: 'bg-green-500/20 text-green-400',
  user_enabled: 'bg-green-500/20 text-green-400',
  user_disabled: 'bg-red-500/20 text-red-400',
  role_changed: 'bg-purple-500/20 text-purple-400',
  staff_assigned: 'bg-indigo-500/20 text-indigo-400',
  document_uploaded: 'bg-cyan-500/20 text-cyan-400',
  document_replaced: 'bg-cyan-500/20 text-cyan-400',
  lab_report_created: 'bg-teal-500/20 text-teal-400',
  lab_report_updated: 'bg-teal-500/20 text-teal-400',
  decode_published: 'bg-[#C89B7B]/20 text-[#C89B7B]',
  protocol_published: 'bg-[#4E7A6B]/20 text-[#4E7A6B]',
  report_published: 'bg-indigo-500/20 text-indigo-400',
  onboarding_completed: 'bg-green-500/20 text-green-400',
  caregiver_linked: 'bg-cyan-500/20 text-cyan-400',
};

const PAGE_SIZE = 25;

export default function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [actionTypes, setActionTypes] = useState([]);

  useEffect(() => {
    axios.get(`${API}/audit/actions`).then(r => setActionTypes(r.data.actions)).catch(() => {});
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', PAGE_SIZE);
      params.set('skip', page * PAGE_SIZE);
      if (actionFilter !== 'all') params.set('action', actionFilter);
      if (searchTerm.trim()) params.set('target_id', searchTerm.trim());
      const r = await axios.get(`${API}/audit?${params.toString()}`);
      setLogs(r.data.logs);
      setTotal(r.data.total);
    } catch (e) {
      console.error('Audit fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, [page, actionFilter, searchTerm]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const formatTime = (ts) => {
    const d = new Date(ts);
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-['Outfit'] font-bold text-adm-primary text-adm-primary flex items-center gap-3" data-testid="audit-title">
            <ScrollText className="w-7 h-7 text-[#C89B7B]" />
            Audit Log
          </h1>
          <p className="text-adm-tertiary text-adm-primary/60 mt-1">Track all administrative actions across the platform</p>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-adm-tertiary text-adm-primary/40" />
            <Input
              placeholder="Search by target ID..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
              className="pl-9 bg-adm-surface bg-adm-surface border-white/[0.06] border-white/[0.06]"
              data-testid="audit-search"
            />
          </div>
          <Select value={actionFilter} onValueChange={(v) => { setActionFilter(v); setPage(0); }}>
            <SelectTrigger className="w-full sm:w-[220px] bg-adm-surface bg-adm-surface border-white/[0.06] border-white/[0.06]" data-testid="audit-action-filter">
              <Filter className="w-4 h-4 mr-2 text-adm-tertiary text-adm-primary/40" />
              <SelectValue placeholder="Filter by action" />
            </SelectTrigger>
            <SelectContent className="bg-adm-surface bg-adm-surface border-white/[0.06] border-white/[0.06] max-h-64">
              <SelectItem value="all">All Actions</SelectItem>
              {actionTypes.map(a => (
                <SelectItem key={a} value={a}>{a.replace(/_/g, ' ')}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <Card className="admin-card bg-adm-surface border-white/[0.06]">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="border-white/[0.06] border-white/[0.06]">
                  <TableHead className="text-adm-secondary text-adm-primary/60 w-[160px]">Time</TableHead>
                  <TableHead className="text-adm-secondary text-adm-primary/60">Action</TableHead>
                  <TableHead className="text-adm-secondary text-adm-primary/60">Description</TableHead>
                  <TableHead className="text-adm-secondary text-adm-primary/60">Actor</TableHead>
                  <TableHead className="text-adm-secondary text-adm-primary/60">Target</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-12 text-adm-tertiary text-adm-primary/40">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-12 text-adm-tertiary text-adm-primary/40" data-testid="audit-empty">
                      No audit records found
                    </TableCell>
                  </TableRow>
                ) : logs.map((log) => (
                  <TableRow key={log.id} className="border-white/[0.06] border-white/[0.06]" data-testid={`audit-row-${log.id}`}>
                    <TableCell className="text-xs text-adm-tertiary text-adm-primary/50 whitespace-nowrap">
                      {formatTime(log.timestamp)}
                    </TableCell>
                    <TableCell>
                      <Badge className={`text-xs capitalize ${actionColors[log.action] || 'bg-adm-bg0/20 text-adm-tertiary'}`}>
                        {log.action?.replace(/_/g, ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-slate-700 text-adm-primary/80 max-w-[300px] truncate">
                      {log.description}
                    </TableCell>
                    <TableCell className="text-xs text-adm-tertiary text-adm-primary/50">
                      <span className="capitalize">{log.actor_role}</span>
                    </TableCell>
                    <TableCell className="text-xs text-adm-tertiary text-adm-primary/50 font-mono">
                      {log.target_id?.substring(0, 20)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-adm-tertiary text-adm-primary/50">
              Showing {page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="border-white/[0.06] border-white/[0.06]"
                data-testid="audit-prev-page"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="border-white/[0.06] border-white/[0.06]"
                data-testid="audit-next-page"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
